import { VxNode } from './parser.js';
import { traverseVxNode } from './traverse.js';

export interface VxRenderOptions {
    renderStyle?: 'html' | 'text';
    renderTagNames?: boolean;
    renderIds?: boolean;
    renderClassNames?: boolean;
    renderRefs?: boolean;
    renderAttributes?: boolean;
    filterAttributes?: (node: VxNode, k: string, v: string) => boolean;
    skipTags?: string[];
    skipNonInteractive?: boolean;
}

export function renderVxNode(scope: VxNode, options: VxRenderOptions = {}) {
    const buffer = [];
    for (const { vxNode, depth } of traverseVxNode(scope)) {
        if (options.skipNonInteractive && !vxNode.isInteractive) {
            continue;
        }
        if (options.skipTags && options.skipTags.includes(vxNode.tagName ?? 'text')) {
            continue;
        }
        const indent = options.skipNonInteractive ? '' : '    '.repeat(depth);
        buffer.push(renderIndentedLine(indent, vxNode, options));
    }
    return buffer.join('\n');
}

function renderIndentedLine(indent: string, vxNode: VxNode, options: VxRenderOptions = {}) {
    if (!vxNode.tagName) {
        return [indent, vxNode.textContent].filter(Boolean).join('');
    }
    const tagLine = renderTagLine(vxNode, options);
    const htmlStyle = options.renderStyle === 'html';
    return [
        indent,
        tagLine,
        htmlStyle ? '' : ' ',
        vxNode.textContent ?? '',
    ].join('');
}

function renderTagLine(vxNode: VxNode, options: VxRenderOptions) {
    const htmlStyle = options.renderStyle === 'html';
    const components: Array<string | null> = [];
    if (options.renderTagNames && vxNode.tagName) {
        components.push(vxNode.tagName.toUpperCase());
    }
    if (options.renderRefs) {
        components.push(`[ref=${vxNode.ref}]`);
    }
    if (options.renderIds && vxNode.id) {
        if (htmlStyle) {
            components.push(`id="${vxNode.id}"`);
        } else {
            components.push(`#${vxNode.id}`);
        }
    }
    if (options.renderClassNames && vxNode.classList?.length) {
        if (htmlStyle) {
            components.push(`class="${vxNode.classList.join(' ')}"`);
        } else {
            components.push('.' + vxNode.classList.join('.'));
        }
    }
    const attrs = [];
    const filterAttributes = options.filterAttributes ?? (() => true);
    if (options.renderAttributes) {
        for (const [attr, value] of Object.entries(vxNode.attributes ?? {})) {
            if (!filterAttributes(vxNode, attr, value)) {
                continue;
            }
            attrs.push(`${attr}="${value}"`);
        }
    }
    if (htmlStyle) {
        components.push(...attrs);
    } else {
        components.push(...attrs.map(attr => `[${attr}]`));
    }
    return htmlStyle ? `<${components.join(' ')}>` : components.join('');
}
